import { useEffect, useState } from "react";
import { Menu, X, ChevronLeft, ChevronRight, Search, MapPin, User } from "lucide-react";
import SiteReveal from "../shared/SiteReveal";
import {
  BRAND as BASE_BRAND,
  NAV,
  NOTICES,
  PROMO_SLIDES,
  REWARDS,
  PROMO_TILES,
  MENU_CATEGORIES,
  MENU_DRINKS,
  MENU_FOOD,
  MENU_GOODS,
  STORES,
  FOOTER_LINKS,
} from "./brewData";
import { getDemoProfile } from "../shared/industryCatalog";
import "../shared/siteReveal.css";
import "./MorningBrewSite.css";

const MENU_MAP = {
  drinks: MENU_DRINKS,
  food: MENU_FOOD,
  goods: MENU_GOODS,
};

function PromoCarousel({ onCta }) {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % PROMO_SLIDES.length), 5000);
    return () => clearInterval(t);
  }, []);

  const slide = PROMO_SLIDES[idx];

  return (
    <section id="mb-top" className="mb-promo">
      <div className="mb-promo__slide" style={{ background: slide.tone }}>
        <div className={`mb-promo__content mb-promo__content--${slide.textTone}`}>
          <span className="mb-promo__eyebrow">{slide.eyebrow}</span>
          <h1>{slide.title}</h1>
          <p>{slide.desc}</p>
          <button type="button" className="mb-btn mb-btn--white" onClick={() => onCta("menu")}>
            {slide.cta}
          </button>
        </div>
        <div className="mb-promo__visual" aria-hidden="true" />
      </div>
      <div className="mb-promo__controls">
        <button type="button" aria-label="이전" onClick={() => setIdx((i) => (i - 1 + PROMO_SLIDES.length) % PROMO_SLIDES.length)}>
          <ChevronLeft size={18} />
        </button>
        <div className="mb-promo__dots">
          {PROMO_SLIDES.map((s, i) => (
            <button
              key={s.id}
              type="button"
              aria-label={`슬라이드 ${i + 1}`}
              className={i === idx ? "is-active" : ""}
              onClick={() => setIdx(i)}
            />
          ))}
        </div>
        <button type="button" aria-label="다음" onClick={() => setIdx((i) => (i + 1) % PROMO_SLIDES.length)}>
          <ChevronRight size={18} />
        </button>
      </div>
    </section>
  );
}

export default function MorningBrewSite({ embed = false, profileId = "morning-brew" }) {
  const profile = getDemoProfile("cafe", profileId);
  const BRAND = profile?.brand ?? BASE_BRAND;
  const theme = profile?.theme ?? {};
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuTab, setMenuTab] = useState("drinks");
  const [storeQ, setStoreQ] = useState("");
  const [loginModal, setLoginModal] = useState(false);
  const [reserveModal, setReserveModal] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", date: "", time: "", guests: "2" });

  const scroll = (id) => {
    setMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const submitReserve = (e) => {
    e.preventDefault();
    setReserveModal(true);
    setForm({ name: "", phone: "", date: "", time: "", guests: "2" });
  };

  const filteredStores = STORES.filter(
    (s) => !storeQ || s.name.includes(storeQ) || s.addr.includes(storeQ),
  );

  const menuItems = MENU_MAP[menuTab] || MENU_DRINKS;

  return (
    <div
      className={`mb-site${embed ? " mb-site--embed" : ""}`}
      style={{
        "--mb-green": theme.green ?? "#00704a",
        "--mb-green-dark": theme.greenDark ?? "#1e3932",
        "--mb-green-light": theme.greenLight ?? "#d4e9e2",
        "--mb-gold": theme.gold ?? "#cba258",
      }}
    >
      <div className="mb-utility">
        <div className="mb-utility__inner">
          <button type="button" className="mb-utility__link" onClick={() => setLoginModal(true)}>
            <User size={14} />
            Sign In
          </button>
          <button type="button" className="mb-utility__link" onClick={() => setLoginModal(true)}>
            Join Us
          </button>
          <button type="button" className="mb-utility__link" onClick={() => scroll("store")}>
            <MapPin size={14} />
            Find a Store
          </button>
        </div>
      </div>

      <header className="mb-header">
        <div className="mb-header__inner">
          <button type="button" className="mb-menu" onClick={() => setMenuOpen((v) => !v)} aria-label="메뉴">
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
          <a href="#mb-top" className="mb-brand" onClick={(e) => { e.preventDefault(); scroll("mb-top"); }}>
            <span className="mb-brand__mark" aria-hidden="true" />
            <strong>{BRAND.name}</strong>
          </a>
          <nav className="mb-nav">
            {NAV.map((n) => (
              <a key={n.id} href={`#${n.id}`} onClick={(e) => { e.preventDefault(); scroll(n.id); }}>
                {n.label}
              </a>
            ))}
          </nav>
          <button type="button" className="mb-search" aria-label="검색">
            <Search size={18} />
          </button>
        </div>
        {menuOpen && (
          <nav className="mb-mobile">
            {NAV.map((n) => (
              <a key={n.id} href={`#${n.id}`} onClick={(e) => { e.preventDefault(); scroll(n.id); }}>
                {n.label}
              </a>
            ))}
          </nav>
        )}
      </header>

      <div className="mb-notice">
        <div className="mb-notice__inner">
          <span className="mb-notice__label">공지</span>
          <p>{NOTICES[0].title}</p>
          <button type="button">더보기</button>
        </div>
      </div>

      <PromoCarousel onCta={scroll} />

      <section id="rewards" className="mb-rewards">
        <div className="mb-wrap mb-rewards__grid">
          <div>
            <span className="mb-rewards__eyebrow">Morning Brew Rewards</span>
            <h2>
              모닝브루만의 특별한 혜택,
              <br />
              <strong>{REWARDS.title}</strong>
            </h2>
            <p>회원가입 후 모닝브루 모바일 카드를 등록하고 리워드 혜택을 누려보세요.</p>
            <div className="mb-rewards__btns">
              <button type="button" className="mb-btn mb-btn--white" onClick={() => setLoginModal(true)}>회원가입</button>
              <button type="button" className="mb-btn mb-btn--outline-white" onClick={() => setLoginModal(true)}>로그인</button>
            </div>
          </div>
          <div className="mb-rewards__card">
            <span className="mb-rewards__card-label">모닝브루 카드</span>
            <strong>0</strong>
            <span>별 적립</span>
            <ul>
              {REWARDS.benefits.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
            <button type="button" className="mb-btn mb-btn--gold">모바일 카드 선물하기</button>
          </div>
        </div>
      </section>

      <section className="mb-tiles">
        <div className="mb-wrap mb-tiles__grid">
          {PROMO_TILES.map((t) => (
            <SiteReveal key={t.title}>
              <article className="mb-tile" style={{ background: t.tone }}>
                <h3>{t.title}</h3>
                <p>{t.desc}</p>
                <button
                  type="button"
                  className="mb-btn mb-btn--white"
                  onClick={() => scroll(t.title.includes("매장") ? "store" : "coffee")}
                >
                  {t.cta}
                </button>
              </article>
            </SiteReveal>
          ))}
        </div>
      </section>

      <section id="coffee" className="mb-section mb-section--gray">
        <div className="mb-wrap">
          <SiteReveal>
            <div className="mb-head">
              <h2>커피 이야기</h2>
              <p>농장에서 우리의 손으로 — 직접 로스팅하는 이유</p>
            </div>
          </SiteReveal>
          <SiteReveal>
            <div className="mb-coffee-story">
              <div className="mb-coffee-story__img" />
              <div>
                <h3>최상의 아라비카 원두</h3>
                <p>
                  모닝브루는 매주 소량 로스팅해 원두 본연의 향을 지킵니다. 바리스타가 추출 방식까지 직접 관리하며,
                  같은 원두도 매일 다른 기분으로 즐길 수 있도록 합니다.
                </p>
                <ul>
                  <li>주 2회 소량 로스팅</li>
                  <li>싱글 오리진 4종 상시 운영</li>
                  <li>리저브 라인 분기별 한정 출시</li>
                </ul>
              </div>
            </div>
          </SiteReveal>
        </div>
      </section>

      <section id="menu" className="mb-section">
        <div className="mb-wrap">
          <SiteReveal>
            <div className="mb-head">
              <h2>메뉴</h2>
              <p>음료 · 푸드 · 상품</p>
            </div>
          </SiteReveal>
          <div className="mb-menu-tabs">
            {MENU_CATEGORIES.map((c) => (
              <button
                key={c.id}
                type="button"
                className={menuTab === c.id ? "is-active" : ""}
                onClick={() => setMenuTab(c.id)}
              >
                {c.label}
              </button>
            ))}
          </div>
          <div className="mb-menu-grid">
            {menuItems.map((m) => (
              <SiteReveal key={m.name}>
                <article className="mb-menu-card">
                  <div className={`mb-menu-card__img mb-menu-card__img--${m.img}`} />
                  <span className="mb-menu-card__cat">{m.cat}</span>
                  <h4>{m.name}</h4>
                  <strong>{m.price}</strong>
                </article>
              </SiteReveal>
            ))}
          </div>
        </div>
      </section>

      <section id="store" className="mb-section mb-section--gray">
        <div className="mb-wrap">
          <SiteReveal>
            <div className="mb-store-hero">
              <div>
                <h2>가까운 매장을 찾아보세요</h2>
                <p>연남본점 · 홍대점 · 망원점 — 편리하게 방문하세요.</p>
              </div>
              <div className="mb-store-search">
                <Search size={16} />
                <input
                  type="text"
                  placeholder="지역 또는 매장명 검색"
                  value={storeQ}
                  onChange={(e) => setStoreQ(e.target.value)}
                />
              </div>
            </div>
          </SiteReveal>
          <div className="mb-store-list">
            {filteredStores.map((s) => (
              <SiteReveal key={s.name}>
                <article className="mb-store-card">
                  <div>
                    <span className="mb-store-card__type">{s.type}</span>
                    <h4>{s.name}</h4>
                    <p>{s.addr}</p>
                    <span>{s.hours}</span>
                  </div>
                  <button type="button" className="mb-btn mb-btn--green" onClick={() => scroll("reserve")}>
                    좌석 예약
                  </button>
                </article>
              </SiteReveal>
            ))}
          </div>
          <SiteReveal>
            <div className="mb-map">지도 영역 (Placeholder)</div>
          </SiteReveal>
        </div>
      </section>

      <section id="reserve" className="mb-section">
        <div className="mb-wrap mb-reserve">
          <SiteReveal>
            <div className="mb-head mb-head--left">
              <h2>좌석 예약</h2>
              <p>2인 이상 방문 시 사전 예약을 권장합니다. · {BRAND.phone}</p>
            </div>
          </SiteReveal>
          <SiteReveal>
            <form className="mb-form" onSubmit={submitReserve}>
              <div className="mb-form-row">
                <div><label>이름</label><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div><label>연락처</label><input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              </div>
              <div className="mb-form-row">
                <div><label>날짜</label><input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
                <div>
                  <label>시간</label>
                  <select required value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })}>
                    <option value="">선택</option>
                    <option>10:00</option><option>12:00</option><option>14:00</option><option>16:00</option><option>18:00</option>
                  </select>
                </div>
              </div>
              <div><label>인원</label><select value={form.guests} onChange={(e) => setForm({ ...form, guests: e.target.value })}><option>2</option><option>3</option><option>4</option><option>5+</option></select></div>
              <button type="submit" className="mb-btn mb-btn--green">예약 신청</button>
            </form>
          </SiteReveal>
        </div>
      </section>

      <footer className="mb-footer">
        <div className="mb-wrap mb-footer__grid">
          <div>
            <strong>{BRAND.name}</strong>
            <p>{BRAND.address}</p>
            <p>TEL {BRAND.phone} · {BRAND.hours}</p>
          </div>
          <div>
            <span>Customer Service</span>
            {FOOTER_LINKS.service.map((l) => <button key={l} type="button">{l}</button>)}
          </div>
          <div>
            <span>Company</span>
            {FOOTER_LINKS.company.map((l) => <button key={l} type="button">{l}</button>)}
          </div>
          <div>
            <span>Policy</span>
            {FOOTER_LINKS.policy.map((l) => <button key={l} type="button">{l}</button>)}
          </div>
        </div>
        <div className="mb-wrap mb-footer__copy">
          © 2026 {BRAND.nameEn}. Portfolio by DSiteSolution
        </div>
      </footer>

      {loginModal && (
        <div className="mb-modal-overlay" onClick={() => setLoginModal(false)}>
          <div className="mb-modal" onClick={(e) => e.stopPropagation()}>
            <h3>모닝브루 리워드</h3>
            <p>회원 전용 혜택 UI입니다. 실제 로그인·가입은 연동되지 않습니다.</p>
            <button type="button" className="mb-btn mb-btn--green" onClick={() => setLoginModal(false)}>확인</button>
          </div>
        </div>
      )}

      {reserveModal && (
        <div className="mb-modal-overlay" onClick={() => setReserveModal(false)}>
          <div className="mb-modal" onClick={(e) => e.stopPropagation()}>
            <h3>예약이 접수되었습니다</h3>
            <p>매장에서 확인 후 연락드리겠습니다.</p>
            <button type="button" className="mb-btn mb-btn--green" onClick={() => setReserveModal(false)}>확인</button>
          </div>
        </div>
      )}
    </div>
  );
}
